/**
 * Financial Calculation Service
 * Financial modeling for solar PV systems in Saudi Arabia.
 *
 * Requested formulas:
 * 1) firstYearSavings = tariff * annualProduction
 * 2) savings25Year = firstYearSavings * 25 * 0.9
 */

import { safeLog } from '../utils/logger';

/** Legacy constant retained for backward compatibility. */
export const ANNUAL_DEGRADATION = 0.005;
/** Legacy constant retained for backward compatibility. */
export const TARIFF_ESCALATION = 0.02;
/** Number of years for lifetime savings projection. */
export const PROJECTION_YEARS = 25;
/** 25-year multiplier requested by product: 25 years with 10% global reduction. */
export const SAVINGS_25Y_FACTOR = 0.9;

export interface FinancialInputs {
  systemSize: number; // kW
  annualProduction: number; // kWh (fallback if monthlyProduction not provided)
  yearlyConsumption: number; // kWh (fallback if monthlyConsumption not provided)
  totalCost: number; // SAR (from BOQ)
  userType: 'residential' | 'commercial' | 'industrial' | 'agricultural';
  financingType: 'cash' | 'loan' | 'lease';
  customTariffSarPerKwh?: number; // Optional user-provided tariff override
  monthlyConsumption?: number[]; // Optional 12-month array (kWh)
  monthlyProduction?: number[]; // Optional 12-month array (kWh)
}

export interface FinancialResults {
  totalCost: number; // SAR (from BOQ incl. VAT)
  financingType: string;
  loanDetails?: {
    downPayment: number; // SAR
    loanAmount: number; // SAR
    interestRate: number; // %
    termYears: number;
    monthlyPayment: number; // SAR
  };

  electricityRate: number; // SAR/kWh (average tariff used in requested formulas)
  firstYearSavings: number; // SAR = tariff * annualProduction
  savings25Year: number; // SAR = firstYearSavings * 25 * 0.9
  netMeteringSavings: number; // SAR (reserved for future export credit)

  annualOPEX: {
    cleaning: number;
    insurance: number;
    maintenance: number;
    monitoring: number;
    total: number;
  };

  paybackPeriod: number; // years (simple payback = totalCost / firstYearSavings)
  roi: number; // % (25-year)
  irr: number; // % (not modeled)
  npv: number; // SAR (not modeled)
  lcoe: number; // SAR/kWh (not modeled)
}

// SEC tariff tables
const TIER1_LIMIT_KWH_PER_MONTH = 6000;

const TIERED_TARIFFS_SAR_PER_KWH = {
  residential: { tier1: 0.18, tier2: 0.30 },
  commercial: { tier1: 0.22, tier2: 0.32 },
  agricultural: { tier1: 0.18, tier2: 0.22 },
} as const;

const INDUSTRIAL_TARIFF_SAR_PER_KWH = 0.20;

// Loan defaults
const LOAN_INTEREST_RATE = 0.045;
const LOAN_TERM_YEARS = 10;
const LOAN_DOWN_PAYMENT_PERCENT = 0.20;

function isValidMonthlyArray(arr: unknown): arr is number[] {
  return (
    Array.isArray(arr) &&
    arr.length === 12 &&
    arr.every((v) => typeof v === 'number' && Number.isFinite(v))
  );
}

function normalizeMonthlyArray(arr: number[] | undefined, fallbackAnnualTotal: number): number[] {
  if (isValidMonthlyArray(arr) && arr.some((v) => v > 0)) return arr;
  const total = Number.isFinite(fallbackAnnualTotal) && fallbackAnnualTotal > 0 ? fallbackAnnualTotal : 0;
  return Array(12).fill(total / 12);
}

function sum12(arr: number[]): number {
  return arr.reduce((s, v) => s + v, 0);
}

/** Compute monthly electricity bill for a given consumption using SEC tiered tariffs. */
export function monthlyBillCostSar(
  userType: FinancialInputs['userType'],
  monthConsumptionKWh: number
): number {
  const c = Math.max(0, monthConsumptionKWh);
  if (userType === 'industrial') return c * INDUSTRIAL_TARIFF_SAR_PER_KWH;

  const t = TIERED_TARIFFS_SAR_PER_KWH[userType];
  const tier1 = Math.min(c, TIER1_LIMIT_KWH_PER_MONTH);
  const tier2 = Math.max(0, c - TIER1_LIMIT_KWH_PER_MONTH);
  return tier1 * t.tier1 + tier2 * t.tier2;
}

function defaultTariffSarPerKwh(userType: FinancialInputs['userType']): number {
  if (userType === 'industrial') return INDUSTRIAL_TARIFF_SAR_PER_KWH;
  return TIERED_TARIFFS_SAR_PER_KWH[userType].tier1;
}

export function calculateFinancials(inputs: FinancialInputs): FinancialResults {
  const totalCost = Number.isFinite(inputs.totalCost) && inputs.totalCost > 0 ? inputs.totalCost : 0;
  const customTariffSarPerKwh =
    typeof inputs.customTariffSarPerKwh === 'number' &&
    Number.isFinite(inputs.customTariffSarPerKwh) &&
    inputs.customTariffSarPerKwh > 0
      ? inputs.customTariffSarPerKwh
      : null;

  // 1. Normalize month-by-month arrays
  const monthlyConsumption = normalizeMonthlyArray(inputs.monthlyConsumption, inputs.yearlyConsumption);
  const monthlyProduction = normalizeMonthlyArray(inputs.monthlyProduction, inputs.annualProduction);
  const annualConsumption = Math.max(0, sum12(monthlyConsumption));
  const annualProduction = Math.max(0, sum12(monthlyProduction));

  // 2. Tariff = annual bill / annual consumption (fallback to default SEC tariff)
  const annualBill = monthlyConsumption.reduce(
    (sum, monthKwh) => sum + monthlyBillCostSar(inputs.userType, Math.max(0, monthKwh)),
    0
  );
  const electricityRate = customTariffSarPerKwh != null
    ? customTariffSarPerKwh
    : annualConsumption > 0
      ? annualBill / annualConsumption
      : defaultTariffSarPerKwh(inputs.userType);

  // 3. Requested savings formulas
  const firstYearSavings = electricityRate * annualProduction;
  const savings25Year = firstYearSavings * PROJECTION_YEARS * SAVINGS_25Y_FACTOR;

  safeLog(
    `[financials] Simple savings (${inputs.userType}): ` +
      `firstYearSavings=${firstYearSavings.toFixed(0)} SAR, ` +
      `annualProduction=${annualProduction.toFixed(0)} kWh, ` +
      `tariff=${electricityRate.toFixed(4)} SAR/kWh`,
    {
      userType: inputs.userType,
      yearlyConsumptionKwh: annualConsumption,
      annualProductionKwh: annualProduction,
      firstYearSavings,
      tariffSarPerKwh: electricityRate,
    }
  );

  // 4. Simple payback
  const paybackPeriod = firstYearSavings > 0 ? totalCost / firstYearSavings : 0;

  // 5. ROI
  const roi = totalCost > 0 ? ((savings25Year - totalCost) / totalCost) * 100 : 0;

  // 6. Loan details
  let loanDetails: FinancialResults['loanDetails'];
  if (inputs.financingType === 'loan') {
    const downPayment = totalCost * LOAN_DOWN_PAYMENT_PERCENT;
    const loanAmount = totalCost - downPayment;
    const monthlyInterestRate = LOAN_INTEREST_RATE / 12;
    const numberOfPayments = LOAN_TERM_YEARS * 12;
    const monthlyPayment =
      loanAmount *
      ((monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1));

    loanDetails = {
      downPayment,
      loanAmount,
      interestRate: LOAN_INTEREST_RATE * 100,
      termYears: LOAN_TERM_YEARS,
      monthlyPayment,
    };
  }

  const annualOPEX: FinancialResults['annualOPEX'] = {
    cleaning: 0,
    insurance: 0,
    maintenance: 0,
    monitoring: 0,
    total: 0,
  };

  return {
    totalCost,
    financingType: inputs.financingType,
    loanDetails,
    electricityRate,
    firstYearSavings,
    savings25Year,
    netMeteringSavings: 0,
    annualOPEX,
    paybackPeriod,
    roi,
    irr: 0,
    npv: 0,
    lcoe: 0,
  };
}
